import type { ApiError } from '@/shared/api';

import { AppLocale } from './types';

// The chat service can return this stable domain error before its OpenAPI schema is refreshed.
type ApiErrorCode = ApiError['code'] | 'auth.password_unchanged' | 'chat.slug_taken';
type ApiErrorMessages = Record<ApiErrorCode, string> & { fallback: string };

const apiErrorMessages: Record<AppLocale, ApiErrorMessages> = {
  [AppLocale.RU]: {
    fallback: 'Не удалось выполнить действие. Попробуйте ещё раз',
    'attachment.empty': 'Файл вложения пуст',
    'attachment.file_missing': 'Выберите файл вложения',
    'attachment.files_missing': 'Выберите хотя бы один файл',
    'attachment.invalid': 'Не удалось обработать вложение',
    'attachment.invalid_attachment_id': 'Некорректный идентификатор вложения',
    'attachment.invalid_type': 'Тип вложения не поддерживается',
    'attachment.too_large': 'Размер вложения превышает допустимый лимит',
    'auth.avatar_file_empty': 'Файл аватара пуст',
    'auth.avatar_file_missing': 'Выберите файл аватара',
    'auth.avatar_file_too_large': 'Размер файла аватара не должен превышать {max_bytes} байт',
    'auth.avatar_invalid_type': 'Можно загрузить только изображение',
    'auth.account_code_invalid': 'Не удалось подтвердить способ входа. Попробуйте ещё раз',
    'auth.email_already_exists': 'Пользователь с таким email уже существует',
    'auth.email_delivery_unavailable': 'Не удалось отправить письмо. Попробуйте позже',
    'auth.email_not_verified': 'Подтвердите email перед входом',
    'auth.onboarding_action_denied': 'Это действие недоступно для текущей сессии onboarding',
    'auth.onboarding_email_mismatch': 'Указанный email не совпадает с email аккаунта',
    'auth.onboarding_session_invalid': 'Сессия заполнения профиля истекла. Войдите снова',
    'auth.oauth_authentication_failed': 'Не удалось завершить вход через внешний сервис',
    'auth.oauth_product_return_invalid': 'Не удалось безопасно вернуться в приложение',
    'auth.oauth_provider_unavailable': 'Этот способ входа сейчас недоступен',
    'auth.oauth_state_invalid': 'Ссылка для входа устарела или уже была использована',
    'auth.origin_not_allowed': 'Запрос с этого адреса не разрешён',
    'auth.last_login_method': 'Нельзя удалить последний способ входа',
    'auth.oauth_identity_already_linked': 'Этот OAuth-аккаунт уже привязан к другому пользователю',
    'auth.oauth_identity_not_found': 'OAuth-привязка не найдена',
    'auth.profile_completion_required': 'Заполните обязательные поля профиля',
    'auth.rate_limited': 'Слишком много запросов. Попробуйте позже',
    'auth.internal_error': 'Не удалось выполнить действие. Попробуйте ещё раз',
    'auth.internal_only': 'Это действие недоступно',
    'auth.invalid_access_token': 'Сессия истекла. Войдите снова',
    'auth.invalid_credentials': 'Неверный email, логин или пароль',
    'auth.invalid_current_password': 'Неверный текущий пароль',
    'auth.password_unchanged': 'Новый пароль должен отличаться от текущего',
    'auth.invalid_refresh_token': 'Сессия истекла. Войдите снова',
    'auth.invalid_verification_code': 'Неверный или просроченный код подтверждения',
    'auth.service_unavailable': 'Сервис временно недоступен. Попробуйте позже',
    'auth.unauthorized': 'Войдите, чтобы продолжить',
    'auth.user_not_found': 'Пользователь не найден',
    'auth.username_already_exists': 'Этот логин уже занят',
    'chat.access_denied': 'У вас нет доступа к этому чату',
    'chat.internal_error': 'Не удалось выполнить действие. Попробуйте ещё раз',
    'chat.invalid_access_token': 'Сессия истекла. Войдите снова',
    'chat.invalid_chat_id': 'Некорректный идентификатор чата',
    'chat.not_found': 'Чат не найден',
    'chat.service_unavailable': 'Сервис временно недоступен. Попробуйте позже',
    'chat.slug_taken': 'Чат с таким адресом уже существует',
    'chat.unauthorized': 'Войдите, чтобы продолжить',
    'message.invalid_content': 'Введите текст сообщения',
    'message.invalid_client_message_id': 'Некорректный идентификатор сообщения',
    'message.invalid_cursor': 'Некорректный курсор сообщений',
    'message.invalid_message_id': 'Некорректный идентификатор сообщения',
    'message.invalid_page_limit': 'Количество сообщений должно быть от 1 до 100',
    'message.invalid_search_query': 'Введите текст для поиска',
    'message.invalid_reply_message_id': 'Некорректный идентификатор сообщения для ответа',
    'message.not_found': 'Сообщение не найдено',
    message_unavailable: 'Сообщение недоступно',
    'message.reply_message_wrong_chat': 'Нельзя ответить на сообщение из другого чата',
    'notification.invalid_mode': 'Некорректный режим уведомлений',
    'notification.invalid_mute_expiration': 'Некорректное время отключения уведомлений',
    'notification.invalid_push_subscription': 'Некорректная push-подписка',
    'request.form_data_missing': 'Не удалось прочитать данные формы',
    'reaction.invalid_emoji': 'Некорректная emoji-реакция',
    'validation.request_invalid': 'Проверьте корректность заполнения полей',
  },
  [AppLocale.EN]: {
    fallback: 'Unable to complete the action. Please try again.',
    'attachment.empty': 'The attachment file is empty',
    'attachment.file_missing': 'Choose an attachment file',
    'attachment.files_missing': 'Choose at least one attachment',
    'attachment.invalid': 'Unable to process the attachment',
    'attachment.invalid_attachment_id': 'Invalid attachment identifier',
    'attachment.invalid_type': 'This attachment type is not supported',
    'attachment.too_large': 'The attachment exceeds the allowed size',
    'auth.avatar_file_empty': 'The avatar file is empty',
    'auth.avatar_file_missing': 'Choose an avatar file',
    'auth.avatar_file_too_large': 'The avatar file must not exceed {max_bytes} bytes',
    'auth.avatar_invalid_type': 'Only image files can be uploaded',
    'auth.account_code_invalid': 'Unable to confirm the sign-in method. Please try again.',
    'auth.email_already_exists': 'A user with this email already exists',
    'auth.email_delivery_unavailable': 'Unable to send the email. Please try again later',
    'auth.email_not_verified': 'Verify your email before signing in',
    'auth.onboarding_action_denied':
      'This action is unavailable for the current onboarding session',
    'auth.onboarding_email_mismatch': 'The email does not match the account email',
    'auth.onboarding_session_invalid': 'Your profile completion session has expired. Sign in again',
    'auth.oauth_authentication_failed': 'Unable to complete sign-in with the external service',
    'auth.oauth_product_return_invalid': 'Unable to return to the app safely',
    'auth.oauth_provider_unavailable': 'This sign-in method is currently unavailable',
    'auth.oauth_state_invalid': 'The sign-in link has expired or was already used',
    'auth.origin_not_allowed': 'Requests from this address are not allowed',
    'auth.last_login_method': 'You cannot remove your last sign-in method',
    'auth.oauth_identity_already_linked': 'This OAuth account is linked to another user',
    'auth.oauth_identity_not_found': 'OAuth link was not found',
    'auth.profile_completion_required': 'Complete the required profile fields',
    'auth.rate_limited': 'Too many requests. Please try again later',
    'auth.internal_error': 'Unable to complete the action. Please try again.',
    'auth.internal_only': 'This action is unavailable',
    'auth.invalid_access_token': 'Your session has expired. Sign in again',
    'auth.invalid_credentials': 'Incorrect email, username, or password',
    'auth.invalid_current_password': 'The current password is incorrect',
    'auth.password_unchanged': 'The new password must differ from the current password',
    'auth.invalid_refresh_token': 'Your session has expired. Sign in again',
    'auth.invalid_verification_code': 'The verification code is invalid or expired',
    'auth.service_unavailable': 'The service is temporarily unavailable. Try again later',
    'auth.unauthorized': 'Sign in to continue',
    'auth.user_not_found': 'User not found',
    'auth.username_already_exists': 'This username is already taken',
    'chat.access_denied': 'You do not have access to this chat',
    'chat.internal_error': 'Unable to complete the action. Please try again.',
    'chat.invalid_access_token': 'Your session has expired. Sign in again',
    'chat.invalid_chat_id': 'Invalid chat identifier',
    'chat.not_found': 'Chat not found',
    'chat.service_unavailable': 'The service is temporarily unavailable. Try again later',
    'chat.slug_taken': 'A chat with this address already exists',
    'chat.unauthorized': 'Sign in to continue',
    'message.invalid_content': 'Enter a message',
    'message.invalid_client_message_id': 'Invalid message identifier',
    'message.invalid_cursor': 'Invalid messages cursor',
    'message.invalid_message_id': 'Invalid message identifier',
    'message.invalid_page_limit': 'The number of messages must be between 1 and 100',
    'message.invalid_search_query': 'Enter text to search',
    'message.invalid_reply_message_id': 'Invalid reply message identifier',
    'message.not_found': 'Message not found',
    message_unavailable: 'Message is unavailable',
    'message.reply_message_wrong_chat': 'You cannot reply to a message from another chat',
    'notification.invalid_mode': 'Invalid notification mode',
    'notification.invalid_mute_expiration': 'Invalid notification mute expiration',
    'notification.invalid_push_subscription': 'Invalid push subscription',
    'request.form_data_missing': 'Unable to read the form data',
    'reaction.invalid_emoji': 'Invalid emoji reaction',
    'validation.request_invalid': 'Check that the fields are filled in correctly',
  },
};

const messages = {
  [AppLocale.RU]: {
    common: {
      appearance: 'Оформление',
      back: 'Назад',
      cancel: 'Отменить',
      confirm: 'Подтвердить',
      copied: 'Скопировано',
      close: 'Закрыть',
      delete: 'Удалить',
      empty: '',
      hotkeys: 'Горячие клавиши',
      aboutFamilyChat: 'О {projectName}',
      information: 'Информация',
      language: 'Язык',
      installation: 'Установка',
      loading: 'Загрузка…',
      notifications: 'Уведомления',
      save: 'Сохранить',
      search: 'Поиск',
      settings: 'Настройки',
      yes: 'да',
      no: 'нет',
    },
    seo: {
      chat: { title: 'Чаты', description: 'Приватный семейный мессенджер {projectName}.' },
      privacy: {
        title: 'Конфиденциальность',
        description: 'Политика конфиденциальности {projectName}.',
      },
      unreadMessagesTitle: '({count}) Новые сообщения',
    },
    home: {
      title: 'Все ваши разговоры рядом',
    },
    connection: {
      online: 'Соединение доступно',
      offline: 'Нет соединения с интернетом',
      reconnecting: 'Восстанавливаем соединение…',
      connected: 'Соединение восстановлено',
    },
    cookieConsent: {
      accept: 'Принять все',
      description:
        'Необходимые cookie нужны для входа, безопасности и работы сервиса. Необязательные диагностические технологии используются только с вашего согласия.',
      policyLink: 'Политика конфиденциальности и cookie',
      reject: 'Отклонить необязательные',
      settings: 'Изменить выбор cookie',
      title: 'Использование cookie',
    },
    appearance: {
      dark: 'Тёмная',
      light: 'Светлая',
      system: 'Системная',
      systemDescription: 'Системная тема следует настройкам устройства.',
      title: 'Оформление',
    },
    language: { en: 'English', ru: 'Русский' },
    presence: {
      longAgo: 'был(а) в сети очень давно',
      month: 'был(а) в сети в этом месяце',
      online: 'В сети',
      recent: 'был(а) в сети недавно',
      week: 'был(а) в сети на этой неделе',
      justNow: 'был(а) в сети только что',
      ago: 'был(а) в сети давно',
    },
    messageMenu: {
      reaction: 'Добавить реакцию',
      copy: 'Скопировать',
      delete: 'Удалить',
      edit: 'Редактировать',
      reply: 'Ответить',
    },
    reactions: {
      add: 'Добавить реакцию',
    },
    hotkeys: {
      dismiss: { description: 'Скрыть поиск или закрыть текущий чат', title: 'Закрыть' },
      key: { comma: ',', enter: 'Enter', escape: 'Esc', k: 'K', shift: 'Shift' },
      modifier: { control: 'Ctrl', meta: '⌘' },
      openSearch: { description: 'Открыть поиск по чатам', title: 'Открыть поиск' },
      openSettings: { description: 'Открыть настройки', title: 'Открыть настройки' },
      sendMessage: { description: 'Отправить сообщение', title: 'Отправить сообщение' },
      submitForm: { description: 'Отправить форму', title: 'Отправить форму' },
    },
    privacyPolicy: {
      necessaryDescription:
        'Cookie аутентификации и безопасности необходимы для входа, поддержания сессии и защиты сервиса.',
      necessaryTitle: 'Необходимые cookie',
      operatorTitle: 'Оператор данных',
      optionalDescription:
        'Необязательные диагностические технологии помогают нам улучшать стабильность сервиса и включаются только после вашего согласия. Вы можете изменить выбор в настройках cookie.',
      optionalTitle: 'Необязательные технологии',
      testDataNotice:
        'Это тестовая политика для среды разработки. Перед публичным запуском замените реквизиты оператора на реальные.',
      title: 'Политика конфиденциальности и cookie',
      version: 'Версия {version}',
    },
    error: {
      notFound: {
        description: 'Страница не существует или была перемещена.',
        homeAction: 'На главную',
        title: 'Страница не найдена',
      },
      application: {
        description: 'Попробуйте обновить страницу или вернуться на главную.',
        homeAction: 'На главную',
        retryAction: 'Повторить',
        title: 'Что-то пошло не так',
      },
    },
    chat: {
      copied: 'Скопировано',
      avatarDeleted: 'Аватар удалён',
      avatarUpdated: 'Аватар изменён',
      usernameUpdated: 'Логин изменён',
      usernameCopied: 'Имя пользователя скопировано',
      usernameLabel: 'Имя пользователя',
      messageSendFailed: 'Не удалось отправить сообщение',
      messageUpdateFailed: 'Не удалось изменить сообщение',
      attachmentTooLarge: '{filename}: файл больше 100 МБ',
      avatar: 'Аватар',
      username: 'Логин',
      editing: 'Редактирование',
      retry: 'Повторить',
      typing: 'Печатает...',
      online: 'Онлайн',
      attachmentImage: 'Фото',
      attachmentFile: 'Файл: {name}',
      attachmentImages: '{count} фото',
      attachments: '{count} вложения',
      messagesCursorMissing: 'Сервер не вернул курсор следующей страницы',
      messagesLoadFailed: 'Не удалось загрузить сообщения',
      chatsLoading: 'Загружаем чаты…',
      chatsLoadFailed: 'Не удалось загрузить чаты',
      noChats: 'У вас пока нет чатов',
      startConversation: 'Начните разговор',
      chooseConversation: 'Выберите переписку или найдите, с кем пообщаться.',
      findConversationPartner: 'Найти собеседника',
      findPeople: 'Найти человека',
      cancelReply: 'Отменить ответ',
      removeAttachment: 'Удалить вложение {name}',
      attachFiles: 'Прикрепить файлы',
      copyUsername: 'Скопировать имя пользователя',
      openEmojiPicker: 'Открыть выбор эмодзи',
      replyMessageDeleted: 'Сообщение удалено',
      replyMessageWithoutText: 'Сообщение без текста',
      searchMessages: 'Поиск по сообщениям',
      searchMessagesHint: 'Введите слово или фразу, чтобы найти сообщение',
      noSearchResults: 'Ничего не найдено',
      you: 'Вы',
      member: 'Участник',
    },
    message: {
      deleteConfirmation: 'Вы уверены, что хотите удалить?',
      deleteForEveryone: 'Удалить для всех',
      deleteForMe: 'Удалить только для меня',
    },
    pwa: {
      install: 'Установить',
      installTitle: 'Установите {name}, чтобы открывать его как приложение.',
      bannerTitle: 'Установить {name}?',
      bannerDescription: 'Открывайте чат как отдельное приложение и возвращайтесь к нему быстрее.',
      bannerHint: 'Установка всегда доступна в настройках чата.',
    },
    notifications: {
      title: 'Уведомления',
      description: 'Получайте уведомления о новых сообщениях, когда чат закрыт.',
      enable: 'Включить уведомления',
      disable: 'Выключить уведомления',
      blocked: 'Уведомления заблокированы в настройках сайта. Разрешите их в браузере.',
      unsupported: 'Уведомления недоступны в этом браузере или пока не настроены.',
      preview: 'Показывать текст сообщения',
      registrationFailed:
        'Не удалось зарегистрировать push-подписку. Повторим попытку при восстановлении сети.',
    },
    chatNotifications: {
      title: 'Уведомления чата',
      enableForever: 'Включить навсегда',
      enabledForever: 'Уведомления включены навсегда',
      mutedForever: 'Уведомления отключены навсегда',
      mutedUntil: 'Уведомления отключены до {date}',
      muteOneHour: 'Отключить на 1 час',
      muteUntilTomorrow: 'Отключить до завтра',
      muteForever: 'Отключить навсегда',
    },
    validation: { emailInvalid: 'Некорректный email', required: 'Обязательное поле' },
  },
  [AppLocale.EN]: {
    common: {
      appearance: 'Appearance',
      back: 'Back',
      cancel: 'Cancel',
      confirm: 'Confirm',
      copied: 'Copied',
      close: 'Close',
      delete: 'Delete',
      empty: '',
      hotkeys: 'Keyboard shortcuts',
      aboutFamilyChat: 'About {projectName}',
      information: 'Information',
      language: 'Language',
      installation: 'Installation',
      loading: 'Loading…',
      notifications: 'Notifications',
      save: 'Save',
      search: 'Search',
      settings: 'Settings',
      yes: 'yes',
      no: 'no',
    },
    seo: {
      chat: { title: 'Chats', description: 'Private family messenger {projectName}.' },
      privacy: { title: 'Privacy', description: '{projectName} privacy policy.' },
      unreadMessagesTitle: '({count}) New messages',
    },
    home: {
      title: 'Your conversations, together',
    },
    connection: {
      online: 'Connection is available',
      offline: 'No internet connection',
      reconnecting: 'Restoring connection…',
      connected: 'Connection restored',
    },
    cookieConsent: {
      accept: 'Accept all',
      description:
        'Necessary cookies are required for sign-in, security, and service operation. Optional diagnostic technologies are used only with your consent.',
      policyLink: 'Privacy and cookie policy',
      reject: 'Reject optional',
      settings: 'Change cookie choice',
      title: 'Cookie usage',
    },
    appearance: {
      dark: 'Dark',
      light: 'Light',
      system: 'System',
      systemDescription: 'System theme follows your device setting.',
      title: 'Appearance',
    },
    language: { en: 'English', ru: 'Russian' },
    presence: {
      longAgo: 'Last seen a long time ago',
      month: 'Last seen this month',
      online: 'Online',
      recent: 'Last seen recently',
      week: 'Last seen this week',
      justNow: 'Last seen just now',
      ago: 'Last seen a while ago',
    },
    messageMenu: {
      reaction: 'Add reaction',
      copy: 'Copy',
      delete: 'Delete',
      edit: 'Edit',
      reply: 'Reply',
    },
    reactions: {
      add: 'Add reaction',
    },
    hotkeys: {
      dismiss: { description: 'Hide search or close the current chat', title: 'Close' },
      key: { comma: ',', enter: 'Enter', escape: 'Esc', k: 'K', shift: 'Shift' },
      modifier: { control: 'Ctrl', meta: '⌘' },
      openSearch: { description: 'Open chat search', title: 'Open search' },
      openSettings: { description: 'Open settings', title: 'Open settings' },
      sendMessage: { description: 'Send a message', title: 'Send message' },
      submitForm: { description: 'Submit a form', title: 'Submit form' },
    },
    privacyPolicy: {
      necessaryDescription:
        'Authentication and security cookies are required for sign-in, session maintenance, and service protection.',
      necessaryTitle: 'Necessary cookies',
      operatorTitle: 'Data controller',
      optionalDescription:
        'Optional diagnostic technologies help us improve service stability and are enabled only after your consent. You can change your choice in cookie settings.',
      optionalTitle: 'Optional technologies',
      testDataNotice:
        'This is a test policy for the development environment. Replace the controller details before public release.',
      title: 'Privacy and cookie policy',
      version: 'Version {version}',
    },
    error: {
      notFound: {
        description: 'This page does not exist or has been moved.',
        homeAction: 'Go home',
        title: 'Page not found',
      },
      application: {
        description: 'Try refreshing the page or return to the home page.',
        homeAction: 'Go home',
        retryAction: 'Retry',
        title: 'Something went wrong',
      },
    },
    chat: {
      copied: 'Copied',
      avatarDeleted: 'Avatar deleted',
      avatarUpdated: 'Avatar updated',
      usernameUpdated: 'Username updated',
      usernameCopied: 'Username copied',
      usernameLabel: 'Username',
      messageSendFailed: 'Unable to send the message',
      messageUpdateFailed: 'Unable to update the message',
      attachmentTooLarge: '{filename}: file exceeds 100 MB',
      avatar: 'Avatar',
      username: 'Username',
      editing: 'Editing',
      retry: 'Retry',
      typing: 'Typing...',
      online: 'Online',
      attachmentImage: 'Image',
      attachmentFile: 'File: {name}',
      attachmentImages: '{count} images',
      attachments: '{count} attachments',
      messagesCursorMissing: 'The server did not return the next page cursor',
      messagesLoadFailed: 'Unable to load messages',
      chatsLoading: 'Loading chats…',
      chatsLoadFailed: 'Unable to load chats',
      noChats: 'You do not have any chats yet',
      startConversation: 'Start a conversation',
      chooseConversation: 'Choose a conversation or find someone to talk to.',
      findConversationPartner: 'Find someone',
      findPeople: 'Find someone to chat with',
      cancelReply: 'Cancel reply',
      removeAttachment: 'Remove attachment {name}',
      attachFiles: 'Attach files',
      copyUsername: 'Copy username',
      openEmojiPicker: 'Open emoji picker',
      replyMessageDeleted: 'Message deleted',
      replyMessageWithoutText: 'Message without text',
      searchMessages: 'Search messages',
      searchMessagesHint: 'Type a word or phrase to find a message',
      noSearchResults: 'No messages found',
      you: 'You',
      member: 'Member',
    },
    message: {
      deleteConfirmation: 'Are you sure you want to delete this message?',
      deleteForEveryone: 'Delete for everyone',
      deleteForMe: 'Delete for me',
    },
    pwa: {
      install: 'Install',
      installTitle: 'Install {name} to open it as an app.',
      bannerTitle: 'Install {name}?',
      bannerDescription: 'Use chat as a standalone app and get back to it faster.',
      bannerHint: 'Installation is always available in chat settings.',
    },
    notifications: {
      title: 'Notifications',
      description: 'Receive notifications about new messages when the chat is closed.',
      enable: 'Enable notifications',
      disable: 'Disable notifications',
      blocked: 'Notifications are blocked in site settings. Allow them in your browser.',
      unsupported: 'Notifications are unavailable in this browser or have not been configured yet.',
      preview: 'Show message text',
      registrationFailed:
        'Could not register the push subscription. We will retry when the connection is restored.',
    },
    chatNotifications: {
      title: 'Chat notifications',
      enableForever: 'Enable forever',
      enabledForever: 'Notifications are enabled forever',
      mutedForever: 'Notifications are muted forever',
      mutedUntil: 'Notifications are muted until {date}',
      muteOneHour: 'Mute for 1 hour',
      muteUntilTomorrow: 'Mute until tomorrow',
      muteForever: 'Mute forever',
    },
    validation: { emailInvalid: 'Invalid email', required: 'This field is required' },
  },
} as const;

export { apiErrorMessages, messages };
